import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import {
  useFormContext,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';

export interface FormFieldProps<TFieldValues extends FieldValues> extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'name'
> {
  name: FieldPath<TFieldValues>;
  label: ReactNode;
  description?: ReactNode;
  rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
}

/**
 * Thin wrapper around react-hook-form's `register` + shadcn-style Input/Label.
 * Renders the error message below the field when the form is submitted with
 * an invalid value.
 */
export const FormField = <TFieldValues extends FieldValues>({
  name,
  label,
  description,
  rules,
  className,
  ...inputProps
}: FormFieldProps<TFieldValues>) => {
  const id = useId();
  const {
    register,
    formState: { errors },
  } = useFormContext<TFieldValues>();
  const fieldError = errors[name];
  const errorMessage =
    fieldError && typeof fieldError === 'object' && 'message' in fieldError
      ? (fieldError.message as string | undefined)
      : undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        aria-invalid={fieldError ? 'true' : 'false'}
        className={cn(fieldError && 'border-destructive', className)}
        {...inputProps}
        {...register(name, rules)}
      />
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      {errorMessage ? <p className="text-xs text-destructive">{errorMessage}</p> : null}
    </div>
  );
};
